import * as Sentry from "@sentry/browser";
import { Alert, Button, Col, message, Row, Spin, Collapse } from "antd";
import { FormInstance } from "antd/lib/form";
import { AxiosResponse } from "axios";
import debounce from "lodash.debounce";
import React, { Fragment, RefObject } from "react";
import { RouteComponentProps } from "react-router";
import { withRouter } from "react-router-dom";
import { UploadFile } from "antd/lib/upload/interface";
import BaseForm from "../components/Form/BaseForm";
import ModalCreator from "../components/ModalCreator";
import {
  GET_LOAN_REQUEST_FOR_EDIT,
  UPDATE_LOAN_REQUEST,
  GET_PROVIDERS,
  CHECK_LOAN_BREAKDOWN,
  GET_REQUIRED_LOAN_DOCUMENTS,
} from "../gql/loans";
import { SEARCH_CUSTOMER } from "../gql/customers";
import {
  APIError,
  Customer,
  FormFieldConfig,
  LoanBreakdownData,
  LoanRequest,
  SelectOptionsInterface,
  User,
} from "../helpers/shared-interfaces";
import {
  convertCustomersToSelectOptions,
  generateDisplayErrorMessage,
  getCompanyPetraId,
  handleValidationErrors,
  mapAPIErrorToFormError,
  request,
  responseHandler,
  filterActiveProducts,
  filterActiveSubscriptions,
} from "../helpers/utilities";
import { LoanEligibilityCard } from "../components/LoanEligibilityCard";
import { GET_SALES_PEOPLE } from "../gql/users";
import {
  DEBOUNCE_TIME,
  contributionTypes,
  loanTenureMonths,
  loanTypes,
} from "../helpers/constants";

interface State {
  isLoading: boolean;
  id: string | number;
  loanRequest?: LoanRequest;
  customers: Customer[];
  searchTerm: string;
  isSearching: boolean;
  selectedCustomer?: Customer;
  providers: SelectOptionsInterface[];
  products: SelectOptionsInterface[];
  selectedProviderId?: string;
  selectedProvider?: any;
  rawProviders: any[];
  showCancelModal: boolean;
  selectedSubscription?: any;
  selectedCompanyId?: string;
  selectedProductId?: string;
  selectedLoanType?: string;
  requiredDocuments?: string[];
  isLoadingDocuments: boolean;
  loanBreakdownData?: LoanBreakdownData;
  isCheckingLoanBreakdown: boolean;
  loanBreakdownError?: string;
  ghanaCardFrontUrl?: string;
  ghanaCardBackUrl?: string;
  passportUrl?: string;
  applicationFormUrl?: string;
  threeMonthsPayslipUrl?: string;
  standingOrderUrl?: string;
  threeMonthBankStatementUrl?: string;
  threeMomoStatementUrl?: string;
  oneYearConsistentTier2ContributionUrl?: string;
  clearanceFormUrl?: string;
  settlementQuotationUrl?: string;
  sixMonthsBankStatementUrl?: string;
  sixMonthsPayslipsUrl?: string;
  employmentOfferLetterUrl?: string;
  borrowersMandateUrl?: string;
  guarantorFormsUrl?: string;
  salesPersonSearchTerm: string;
  salesPersons: User[];
  updateLoanError?: string;
}

interface RouteParams {
  id?: string;
}

export interface Props extends RouteComponentProps<RouteParams> {
  name?: string;
  user: User;
  customer?: Customer;
  handleSuccess?: () => void;
}

const fieldsToBeValidated = [
  "customerId",
  "companyId",
  "customerSubscriptionId",
  "providerId",
  "productId",
  "loanType",
  "loanAmountRequested",
  "loanTenureMonths",
  "loanPurpose",
  "portionOfContributionToBeUsed",
  "salesPersonId",
];

class EditLoanRequest extends React.Component<Props, State> {
  reference: RefObject<FormInstance>;
  documentFormRef: RefObject<FormInstance>;

  constructor(props: Props) {
    super(props);
    this.state = {
      isLoading: true,
      loanRequest: undefined,
      id: "",
      customers: [],
      searchTerm: "",
      isSearching: false,
      selectedCustomer: undefined,
      providers: [],
      products: [],
      selectedProviderId: undefined,
      rawProviders: [],
      showCancelModal: false,
      selectedSubscription: undefined,
      selectedCompanyId: undefined,
      loanBreakdownData: undefined,
      isCheckingLoanBreakdown: false,
      loanBreakdownError: undefined,
      isLoadingDocuments: false,
      ghanaCardFrontUrl: undefined,
      ghanaCardBackUrl: undefined,
      passportUrl: undefined,
      applicationFormUrl: undefined,
      threeMonthsPayslipUrl: undefined,
      standingOrderUrl: undefined,
      threeMonthBankStatementUrl: undefined,
      threeMomoStatementUrl: undefined,
      oneYearConsistentTier2ContributionUrl: undefined,
      clearanceFormUrl: undefined,
      settlementQuotationUrl: undefined,
      sixMonthsBankStatementUrl: undefined,
      sixMonthsPayslipsUrl: undefined,
      employmentOfferLetterUrl: undefined,
      borrowersMandateUrl: undefined,
      guarantorFormsUrl: undefined,
      salesPersonSearchTerm: "",
      salesPersons: [],
    };

    this.reference = React.createRef<FormInstance>();
    this.documentFormRef = React.createRef<FormInstance>();
    this.cancelRequest = this.cancelRequest.bind(this);
  }

  componentDidMount(): void {
    this.setState(
      { id: this.props.match.params.id || 0, isLoading: true },
      (): void => {
        this.getLoanRequest();
        this.getAllProviders();
        this.getAllSalesPerson();
      },
    );
  }

  getAllSalesPerson = (): void => {
    request({
      query: GET_SALES_PEOPLE,
      variables: {
        page: 1,
        q: this.state.salesPersonSearchTerm,
      },
    }).then(({ data: { data } }: any): void => {
      this.setState({
        salesPersons: data.salesPeople.results,
      });
    });
  };

  handleSalesPersonSearchInput = async (
    salesPersonSearchTerm: string,
  ): Promise<void> => {
    this.setState({ salesPersonSearchTerm }, () => {
      this.triggerSalesPersonDelayedSearch();
    });
  };

  triggerSalesPersonDelayedSearch = debounce(async (): Promise<void> => {
    this.getAllSalesPerson();
  }, DEBOUNCE_TIME);

  getLoanRequest(): void {
    const { id } = this.state;
    interface Response {
      getLoanRequest: LoanRequest;
    }
    const query = GET_LOAN_REQUEST_FOR_EDIT;
    this.setState({ isLoading: true });
    request({
      query,
      variables: { id },
    })
      .then(({ data }: AxiosResponse<{ data: Response; }>): void => {
        const loanRequest = data?.data?.getLoanRequest;
        const selectedCustomer = loanRequest?.customer;
        const selectedSubscription = loanRequest?.customerSubscription;
        const selectedCompanyId = loanRequest?.company?.id;
        const loanBreakdownData = loanRequest?.loanBreakdown;

        this.setState(
          {
            loanRequest,
            selectedCustomer,
            selectedProviderId: loanRequest?.provider?.id,
            selectedSubscription,
            selectedCompanyId,
            loanBreakdownData,
            selectedLoanType: loanRequest?.loanType,
            selectedProductId: loanRequest?.product?.id,
            ghanaCardFrontUrl: loanRequest?.ghanaCardFrontUrl,
            ghanaCardBackUrl: loanRequest?.ghanaCardBackUrl,
            passportUrl: loanRequest?.passportUrl,
            applicationFormUrl: loanRequest?.applicationFormUrl,
            threeMonthsPayslipUrl: loanRequest?.threeMonthsPayslipUrl,
            standingOrderUrl: loanRequest?.standingOrderUrl,
            threeMonthBankStatementUrl: loanRequest?.threeMonthBankStatementUrl,
            threeMomoStatementUrl: loanRequest?.threeMomoStatementUrl,
            oneYearConsistentTier2ContributionUrl:
              loanRequest?.oneYearConsistentTier2ContributionUrl,
            clearanceFormUrl: loanRequest?.clearanceFormUrl,
            settlementQuotationUrl: loanRequest?.settlementQuotationUrl,
            sixMonthsBankStatementUrl: loanRequest?.sixMonthsBankStatementUrl,
            sixMonthsPayslipsUrl: loanRequest?.sixMonthsPayslipsUrl,
            employmentOfferLetterUrl: loanRequest?.employmentOfferLetterUrl,
            borrowersMandateUrl: loanRequest?.borrowersMandateUrl,
            guarantorFormsUrl: loanRequest?.guarantorFormsUrl,
          },
          () => {
            this.handleProviderSelect(loanRequest?.provider?.id || "");
            this.setFormValues(loanRequest);
          },
        );
      })
      .finally((): void => {
        this.setState({ isLoading: false });
      });
  }

  setFormValues = (loanRequest: LoanRequest): void => {
    if (!this.reference.current || !loanRequest) return;

    this.reference.current.setFieldsValue({
      customerId: loanRequest.customer?.id,
      companyId: loanRequest.company?.id,
      customerSubscriptionId: loanRequest.customerSubscription?.id,
      providerId: loanRequest.provider?.id,
      productId: loanRequest.product?.id,
      loanType: loanRequest.loanType,
      loanAmountRequested: loanRequest.loanAmountRequested,
      loanTenureMonths: String(loanRequest.loanTenureMonths),
      loanPurpose: loanRequest.loanPurpose,
      portionOfContributionToBeUsed: loanRequest.portionOfContributionToBeUsed,
    });

    this.documentFormRef.current?.setFieldsValue({
      ghanaCardFrontUrl: loanRequest.ghanaCardFrontUrl,
      ghanaCardBackUrl: loanRequest.ghanaCardBackUrl,
      passportUrl: loanRequest.passportUrl,
      applicationFormUrl: loanRequest.applicationFormUrl,
      threeMonthsPayslipUrl: loanRequest.threeMonthsPayslipUrl,
      standingOrderUrl: loanRequest.standingOrderUrl,
      threeMonthBankStatementUrl: loanRequest.threeMonthBankStatementUrl,
      threeMomoStatementUrl: loanRequest.threeMomoStatementUrl,
      oneYearConsistentTier2ContributionUrl:
        loanRequest.oneYearConsistentTier2ContributionUrl,
      clearanceFormUrl: loanRequest.clearanceFormUrl,
      settlementQuotationUrl: loanRequest.settlementQuotationUrl,
      sixMonthsBankStatementUrl: loanRequest.sixMonthsBankStatementUrl,
      sixMonthsPayslipsUrl: loanRequest.sixMonthsPayslipsUrl,
      employmentOfferLetterUrl: loanRequest.employmentOfferLetterUrl,
      borrowersMandateUrl: loanRequest.borrowersMandateUrl,
      guarantorFormsUrl: loanRequest.guarantorFormsUrl,
    });
  };

  handleCompanySelect = (companyId: string): void => {
    this.setState({
      selectedCompanyId: companyId,
      loanBreakdownData: undefined,
      loanBreakdownError: undefined,
    });
  };

  handleSubscriptionSelect = (subscriptionId: string): void => {
    const { selectedCustomer, loanRequest } = this.state;
    const currentCustomer = selectedCustomer || loanRequest?.customer;

    if (!currentCustomer) {
      message.error("Please select a customer first");
      return;
    }

    const selectedSubscription = currentCustomer.customerSubscriptions?.find(
      (sub) => sub.id === subscriptionId,
    );

    if (!selectedSubscription) {
      message.error("Subscription not found");
      return;
    }

    this.setState({
      selectedSubscription,
      loanBreakdownData: undefined,
      loanBreakdownError: undefined,
    });

    this.reference.current?.setFieldsValue({
      loanAmountRequested: undefined,
    });
  };

  checkLoanBreakdown = async (): Promise<void> => {
    const {
      selectedCustomer,
      loanRequest,
      selectedSubscription,
      selectedCompanyId,
      selectedProviderId,
    } = this.state;
    const currentCustomer = selectedCustomer || loanRequest?.customer;

    if (!this.reference.current) return;

    let formData: any;
    try {
      formData = await this.reference.current.validateFields([
        "loanAmountRequested",
        "loanType",
        "portionOfContributionToBeUsed",
        "loanTenureMonths",
        "productId",
      ]);
    } catch {
      message.warning("Please fill in required fields to check eligibility");
      return;
    }

    if (!currentCustomer?.petraId) {
      message.warning("Customer Petra ID is missing");
      return;
    }

    if (!selectedSubscription?.product?.fundCode) {
      message.warning("Please select a subscription first");
      return;
    }

    if (!selectedProviderId) {
      message.warning("Please select a provider first");
      return;
    }

    if (!formData.productId) {
      message.warning("Please select a product first");
      return;
    }

    const companyPetraId = getCompanyPetraId(
      currentCustomer,
      selectedCompanyId || "",
    );

    this.setState({
      isCheckingLoanBreakdown: true,
      loanBreakdownError: undefined,
    });

    try {
      const response: AxiosResponse = await request({
        query: CHECK_LOAN_BREAKDOWN,
        variables: {
          companyPetraId: companyPetraId || "",
          customerPetraId: currentCustomer.petraId,
          fundCode: selectedSubscription.product.fundCode,
          loanAmount: formData.loanAmountRequested,
          loanType: formData.loanType,
          portionOfContributionToBeUsed: formData.portionOfContributionToBeUsed,
          endDate: new Date().toISOString().split("T")[0],
          loanTenureMonths: parseInt(formData.loanTenureMonths),
          loanProductId: formData.productId,
        },
      });

      const result = response.data?.data
        ?.checkLoanBreakdown as LoanBreakdownData;

      if (!result) {
        message.error("Failed to get loan breakdown data");
        return;
      }

      if (result.success) {
        this.setState({ loanBreakdownData: result });

        if (result.data.eligible_for_loan) {
          message.success(
            `Maximum loanable amount: GHS ${parseFloat(
              result.data.loanable_amount,
            ).toLocaleString()}`,
          );
        } else {
          message.warning("Customer is not eligible for this loan amount");
        }
      } else {
        const errorMessage = result.error || "Failed to check loan breakdown";
        message.error(errorMessage);
        this.setState({
          loanBreakdownData: undefined,
          loanBreakdownError: errorMessage,
        });
      }
    } catch (error) {
      message.error(`Failed to check loan breakdown - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
      this.setState({
        loanBreakdownData: undefined,
        loanBreakdownError: "An error occurred while checking loan breakdown",
      });
    } finally {
      this.setState({ isCheckingLoanBreakdown: false });
    }
  };

  getAllProviders(): void {
    request({
      query: GET_PROVIDERS,
      variables: {
        page: 1,
        perPage: "100",
      },
    })
      .then(({ data: { data } }: any): void => {
        const providers = data.getProviders || [];

        this.setState({
          providers,
          rawProviders: providers,
        });
      })
      .catch((error) => {
        message.error("Failed to load providers");
        Sentry.captureException(error);
      });
  }

  redirectToViewLoanRequestPage = (id: string): void => {
    this.props.history.push(`/loan-requests/${id}`);
  };

  handleSearchInput = async (searchTerm: string): Promise<void> => {
    this.setState({ searchTerm }, async () => {
      await this.triggerDelayedSearch();
    });
  };

  handleCustomerSelect = async (id: string): Promise<void> => {
    const selectedCustomer = this.state.customers.find(
      (customer: Customer): boolean => customer.id === id,
    );

    this.setState({
      selectedCustomer,
    });
  };

  triggerDelayedSearch = debounce(async (): Promise<void> => {
    await this.triggerCustomerSearch();
  }, DEBOUNCE_TIME);

  triggerCustomerSearch = async (): Promise<void> => {
    const { searchTerm: q } = this.state;
    interface Response {
      count: number;
      results: Customer[];
    }

    this.setState({ isSearching: true });

    try {
      const r: AxiosResponse = await request({
        query: SEARCH_CUSTOMER,
        variables: { q, page: 1, numberPerPage: 50 },
      });

      const { err, mutation } = responseHandler<Response>(r, "customerSearch");

      if (err) {
        message.error(`customer search failed - ${generateDisplayErrorMessage(err)}`);
      } else {
        this.setState({
          customers: mutation.results,
        });
      }
    } catch (error) {
      message.error(`customer search failed - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }

    this.setState({ isSearching: false });
  };

  handleProviderSelect = (id: string): void => {
    const { rawProviders, loanRequest } = this.state;
    const selectedProvider = rawProviders.find((p) => p.id === id);

    const products = filterActiveProducts<any>(
      selectedProvider?.activeProducts,
    );

    this.setState(
      {
        selectedProviderId: id,
        selectedProvider,
        products,
        selectedProductId: undefined,
        requiredDocuments: undefined,
        selectedLoanType: undefined,
      },
      () => {
        // On initial load (provider unchanged from the saved request), restore
        // the documents for the loan's existing product.
        if (
          id === loanRequest?.provider?.id &&
          loanRequest?.product?.id &&
          loanRequest?.loanType
        ) {
          this.setState({ selectedProductId: loanRequest.product.id });
          this.fetchRequiredDocuments(
            loanRequest.product.id,
            loanRequest.loanType,
          );
        }
      },
    );

    this.reference.current?.setFieldsValue({
      productId: undefined,
      loanType: undefined,
    });
  };

  // Required documents now depend on the product (provider + security type are
  // derived from it server-side), so fetch them once both the product and the
  // loan type are known -- whichever is picked last triggers the load.
  handleProductSelect = (productId: string): void => {
    this.setState({ selectedProductId: productId, requiredDocuments: undefined });

    const { selectedLoanType } = this.state;
    if (selectedLoanType) {
      this.fetchRequiredDocuments(productId, selectedLoanType);
    }
  };

  handleLoanTypeSelect = (loanType: string): void => {
    this.setState({ selectedLoanType: loanType, requiredDocuments: undefined });

    const { selectedProductId } = this.state;
    if (selectedProductId) {
      this.fetchRequiredDocuments(selectedProductId, loanType);
    }
  };

  fetchRequiredDocuments = async (
    productId: string,
    loanType: string,
  ): Promise<void> => {
    this.setState({ isLoadingDocuments: true, requiredDocuments: undefined });

    try {
      const response: AxiosResponse = await request({
        query: GET_REQUIRED_LOAN_DOCUMENTS,
        variables: {
          productId,
          loanType,
        },
      });

      const documents = response.data?.data?.getRequiredLoanDocuments || [];

      // Convert document objects to camelCase field names with "Url" suffix
      // e.g., { id: "ghana_card_front", name: "Ghana Card Front" } -> "ghanaCardFrontUrl"
      const requiredDocuments = documents.map(
        (doc: { id: string; name: string; }) => {
          const camelCase = doc.id.replace(/_([a-z])/g, (_, letter: string) =>
            letter.toUpperCase(),
          );
          return `${camelCase}Url`;
        },
      );

      this.setState({
        requiredDocuments,
      });
    } catch (error) {
      message.error(`Failed to load required documents - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
      this.setState({
        requiredDocuments: [],
      });
    } finally {
      this.setState({ isLoadingDocuments: false });
    }
  };

  handleFileUploadChange = (field: string, combinedInfo: string): void => {
    const [url] = combinedInfo.split("@@@");
    this.setState({ [field]: url } as any);
  };

  handleDelete = (file: UploadFile): void => {
    const fileUrl = file.url;
    const stateKeys = [
      "ghanaCardFrontUrl",
      "ghanaCardBackUrl",
      "passportUrl",
      "applicationFormUrl",
      "threeMonthsPayslipUrl",
      "standingOrderUrl",
      "threeMonthBankStatementUrl",
      "threeMomoStatementUrl",
      "oneYearConsistentTier2ContributionUrl",
      "clearanceFormUrl",
      "settlementQuotationUrl",
      "sixMonthsBankStatementUrl",
      "sixMonthsPayslipsUrl",
      "employmentOfferLetterUrl",
      "borrowersMandateUrl",
      "guarantorFormsUrl",
    ];

    stateKeys.forEach((key) => {
      if (this.state[key as keyof State] === fileUrl) {
        this.setState({ [key]: undefined } as any);
      }
    });
  };

  updateLoanRequest = async (): Promise<void> => {
    interface Response {
      loanRequest: LoanRequest;
      errors: APIError[];
    }

    if (!this.reference.current || !this.documentFormRef.current) return;

    let data;

    const shouldRedirect = !this.props.customer;

    try {
      data = await this.reference.current.validateFields(fieldsToBeValidated);
    } catch (error) {
      handleValidationErrors(error as any);
      return;
    }

    const { loanBreakdownData } = this.state;

    if (!loanBreakdownData) {
      message.warning("Please check loan eligibility first");
      return;
    }

    if (!loanBreakdownData.data.eligible_for_loan) {
      message.error("Customer is not eligible for this loan");
      return;
    }

    this.setState({ isLoading: true, updateLoanError: undefined });

    let variables = {
      ...data,
      id: this.state.loanRequest?.id,
      source: "phoenix",
      loanTenureMonths: parseInt(data.loanTenureMonths),
      ghanaCardFrontUrl: this.state.ghanaCardFrontUrl || null,
      ghanaCardBackUrl: this.state.ghanaCardBackUrl || null,
      passportUrl: this.state.passportUrl || null,
      applicationFormUrl: this.state.applicationFormUrl || null,
      threeMonthsPayslipUrl: this.state.threeMonthsPayslipUrl || null,
      standingOrderUrl: this.state.standingOrderUrl || null,
      threeMonthBankStatementUrl: this.state.threeMonthBankStatementUrl || null,
      threeMomoStatementUrl: this.state.threeMomoStatementUrl || null,
      oneYearConsistentTier2ContributionUrl:
        this.state.oneYearConsistentTier2ContributionUrl || null,
      clearanceFormUrl: this.state.clearanceFormUrl || null,
      settlementQuotationUrl: this.state.settlementQuotationUrl || null,
      sixMonthsBankStatementUrl: this.state.sixMonthsBankStatementUrl || null,
      sixMonthsPayslipsUrl: this.state.sixMonthsPayslipsUrl || null,
      employmentOfferLetterUrl: this.state.employmentOfferLetterUrl || null,
      borrowersMandateUrl: this.state.borrowersMandateUrl || null,
      guarantorFormsUrl: this.state.guarantorFormsUrl || null,
      salesPersonId: data.salesPersonId,
    };

    try {
      const response: AxiosResponse = await request({
        query: UPDATE_LOAN_REQUEST,
        variables,
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "updateLoanRequest",
      );

      if (err || mutation.errors?.length) {
        const errors = err || mutation.errors;
        message.error(generateDisplayErrorMessage(errors));
        this.reference.current?.setFields(errors.map(mapAPIErrorToFormError));
        this.setState({ updateLoanError: generateDisplayErrorMessage(errors) });
      } else {
        this.reference.current.resetFields();
        message.success("Loan request updated successfully", 1.5, () => {
          if (shouldRedirect) {
            this.redirectToViewLoanRequestPage(mutation.loanRequest.id);
          }
        });
        this.props.handleSuccess && this.props.handleSuccess();
      }
    } catch (error) {
      message.error(`Failed to update loan request - ${generateDisplayErrorMessage(error)}`);
      this.setState({ updateLoanError: "Failed to update loan request" });
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  toggleCancelModal = async () => {
    this.setState({ showCancelModal: !this.state.showCancelModal });
  };

  cancelRequest = async () => {
    this.props.history.goBack();
  };

  render(): JSX.Element {
    const {
      isLoading,
      loanRequest,
      isSearching,
      customers,
      providers,
      products,
      showCancelModal,
      loanBreakdownData,
      isCheckingLoanBreakdown,
      loanBreakdownError,
      salesPersons,
      updateLoanError,
    } = this.state;

    const currentCustomer =
      this.state.selectedCustomer || loanRequest?.customer;
    const fields: FormFieldConfig[] = [
      {
        id: "customerId",
        name: "customerId",
        inputType: "search",
        required: true,
        placeholder:
          "Search by Email, Phone, SSNIT, First name, Middle name, Last name or PetraID ...",
        rule: "string",
        label: "Customer",
        disabled: !!this.props.customer,
        initialValue: this.props.customer?.id,
        selectOptions: loanRequest?.customer
          ? convertCustomersToSelectOptions([loanRequest?.customer])
          : convertCustomersToSelectOptions(customers),
        changeHandler: this.handleSearchInput,
        selectHandler: this.handleCustomerSelect,
        isSearching,
        colConfig: { span: 24 },
      },
      {
        id: "companyId",
        name: "companyId",
        inputType: "select",
        rule: "string",
        required: true,
        placeholder: "Select Company",
        label: "Select Company",
        selectOptions:
          currentCustomer?.customerCompanyAssociations?.map((association) => ({
            id: association.company.id,
            name: association.company.name,
            value: association.company.id,
          })) || [],
        selectHandler: (id: string) => this.handleCompanySelect(id),
        colConfig: { span: 24 },
      },
      {
        id: "customerSubscriptionId",
        name: "customerSubscriptionId",
        inputType: "select",
        rule: "string",
        required: true,
        placeholder: "Select Customer Subscription",
        label: "Select Customer Subscription",
        selectOptions:
          filterActiveSubscriptions(currentCustomer?.customerSubscriptions).map(
            (subscription) => ({
              id: subscription.id,
              name: subscription.product.name,
              value: subscription.id,
            }),
          ) || [],
        selectHandler: (id: string) => this.handleSubscriptionSelect(id),
        colConfig: { span: 24 },
      },
      {
        id: "portionOfContributionToBeUsed",
        name: "portionOfContributionToBeUsed",
        inputType: "multipleSelect",
        rule: "array",
        required: true,
        placeholder: "Select Portion of Contribution To Be Used",
        label: "Portion of Contribution To Be Used",
        selectOptions: contributionTypes,
        colConfig: { span: 24 },
      },
      {
        id: "providerId",
        name: "providerId",
        inputType: "select",
        rule: "string",
        required: true,
        placeholder: "Select Provider",
        label: "Provider",
        selectOptions: providers,
        selectHandler: this.handleProviderSelect,
        initialValue: loanRequest?.provider?.id,
        colConfig: { span: 24 },
      },
      {
        id: "productId",
        name: "productId",
        inputType: "select",
        rule: "string",
        required: true,
        placeholder: "Select Product",
        label: "Product",
        selectOptions: products,
        initialValue: loanRequest?.product?.id,
        selectHandler: (id: string) => this.handleProductSelect(id),
        colConfig: { span: 24 },
      },
      {
        id: "loanType",
        name: "loanType",
        inputType: "select",
        rule: "string",
        required: true,
        placeholder: "Select Loan Type",
        label: "Loan Type",
        selectOptions: loanTypes,
        selectHandler: (value: string) => this.handleLoanTypeSelect(value),
        colConfig: { span: 24 },
      },
      {
        id: "loanAmountRequested",
        name: "loanAmountRequested",
        inputType: "number",
        rule: "number",
        required: true,
        placeholder: "Enter loan amount",
        label: "Loan Amount Requested",
        initialValue: loanRequest?.loanAmountRequested,
        colConfig: { span: 24 },
        elementStyle: { width: "100%" },
      },
      {
        id: "loanTenureMonths",
        name: "loanTenureMonths",
        inputType: "select",
        rule: "string",
        required: true,
        placeholder: "Select loan tenure",
        label: "Loan Tenure (Months)",
        selectOptions: loanTenureMonths,
        colConfig: { span: 24 },
      },
      {
        id: "loanPurpose",
        name: "loanPurpose",
        inputType: "string",
        rule: "string",
        required: true,
        placeholder: "Enter loan purpose",
        label: "Loan Purpose",
        initialValue: loanRequest?.loanPurpose,
        colConfig: { span: 24 },
      },
      {
        id: "salesPersonId",
        name: "salesPersonId",
        rule: "string",
        inputType: "search",
        required: true,
        label: "Sales Person",
        initialValue: loanRequest?.salesPerson?.id,
        placeholder: "Select sales person",
        selectOptions: salesPersons,
        changeHandler: this.handleSalesPersonSearchInput,
        isSearching,
        colConfig: {
          span: 24,
        },
      },
    ];

    const requiredDocuments = this.state.requiredDocuments || [];

    const documentFields: FormFieldConfig[] = [
      {
        id: "ghanaCardFrontUrl",
        name: "ghanaCardFrontUrl",
        inputType: "fileUpload",
        rule: "string",
        initialValue: this.state.ghanaCardFrontUrl,
        required: false,
        disabled: false,
        label: "Ghana Card (Front)",
        defaultFileList: this.state.ghanaCardFrontUrl
          ? [
            {
              uid: "1",
              name:
                this.state.ghanaCardFrontUrl.split("/").pop() ||
                "Ghana Card (Front)",
              status: "done",
              url: this.state.ghanaCardFrontUrl,
            },
          ]
          : [],
        changeHandler: (combinedInfo: string) =>
          this.handleFileUploadChange("ghanaCardFrontUrl", combinedInfo),
        onFileDelete: this.handleDelete,
        fileUploadProps: {
          accept: "image/*,.pdf,application/pdf",
        },
        colConfig: { span: 10 },
      },
      {
        id: "ghanaCardBackUrl",
        name: "ghanaCardBackUrl",
        inputType: "fileUpload",
        rule: "string",
        initialValue: this.state.ghanaCardBackUrl,
        required: false,
        disabled: false,
        label: "Ghana Card (Back)",
        defaultFileList: this.state.ghanaCardBackUrl
          ? [
            {
              uid: "1",
              name:
                this.state.ghanaCardBackUrl.split("/").pop() ||
                "Ghana Card (Back)",
              status: "done",
              url: this.state.ghanaCardBackUrl,
            },
          ]
          : [],
        changeHandler: (combinedInfo: string) =>
          this.handleFileUploadChange("ghanaCardBackUrl", combinedInfo),
        onFileDelete: this.handleDelete,
        fileUploadProps: {
          accept: "image/*,.pdf,application/pdf",
        },
        colConfig: { span: 10 },
      },
      {
        id: "passportUrl",
        name: "passportUrl",
        inputType: "fileUpload",
        rule: "string",
        initialValue: this.state.passportUrl,
        required: false,
        disabled: false,
        label: "Passport Picture",
        defaultFileList: this.state.passportUrl
          ? [
            {
              uid: "1",
              name: this.state.passportUrl.split("/").pop() || "Passport",
              status: "done",
              url: this.state.passportUrl,
            },
          ]
          : [],
        changeHandler: (combinedInfo: string) =>
          this.handleFileUploadChange("passportUrl", combinedInfo),
        onFileDelete: this.handleDelete,
        fileUploadProps: {
          accept: "image/*,.pdf,application/pdf",
        },
        colConfig: { span: 10 },
      },
      {
        id: "applicationFormUrl",
        name: "applicationFormUrl",
        inputType: "fileUpload",
        rule: "string",
        initialValue: this.state.applicationFormUrl,
        required: false,
        disabled: false,
        label: "Application Form",
        defaultFileList: this.state.applicationFormUrl
          ? [
            {
              uid: "1",
              name:
                this.state.applicationFormUrl.split("/").pop() ||
                "Application Form",
              status: "done",
              url: this.state.applicationFormUrl,
            },
          ]
          : [],
        changeHandler: (combinedInfo: string) =>
          this.handleFileUploadChange("applicationFormUrl", combinedInfo),
        onFileDelete: this.handleDelete,
        fileUploadProps: {
          accept: ".pdf,application/pdf",
        },
        colConfig: { span: 10 },
      },
      {
        id: "threeMonthsPayslipUrl",
        name: "threeMonthsPayslipUrl",
        inputType: "fileUpload",
        rule: "string",
        initialValue: this.state.threeMonthsPayslipUrl,
        required: false,
        disabled: false,
        label: "3 Months Payslip",
        defaultFileList: this.state.threeMonthsPayslipUrl
          ? [
            {
              uid: "1",
              name:
                this.state.threeMonthsPayslipUrl.split("/").pop() ||
                "3 Months Payslip",
              status: "done",
              url: this.state.threeMonthsPayslipUrl,
            },
          ]
          : [],
        changeHandler: (combinedInfo: string) =>
          this.handleFileUploadChange("threeMonthsPayslipUrl", combinedInfo),
        onFileDelete: this.handleDelete,
        fileUploadProps: {
          accept: ".pdf,application/pdf",
        },
        colConfig: { span: 10 },
      },
      {
        id: "standingOrderUrl",
        name: "standingOrderUrl",
        inputType: "fileUpload",
        rule: "string",
        initialValue: this.state.standingOrderUrl,
        required: false,
        disabled: false,
        label: "Standing Order",
        defaultFileList: this.state.standingOrderUrl
          ? [
            {
              uid: "1",
              name:
                this.state.standingOrderUrl.split("/").pop() ||
                "Standing Order",
              status: "done",
              url: this.state.standingOrderUrl,
            },
          ]
          : [],
        changeHandler: (combinedInfo: string) =>
          this.handleFileUploadChange("standingOrderUrl", combinedInfo),
        onFileDelete: this.handleDelete,
        fileUploadProps: {
          accept: ".pdf,application/pdf",
        },
        colConfig: { span: 10 },
      },
      {
        id: "threeMonthBankStatementUrl",
        name: "threeMonthBankStatementUrl",
        inputType: "fileUpload",
        rule: "string",
        initialValue: this.state.threeMonthBankStatementUrl,
        required: false,
        disabled: false,
        label: "3 Month Bank Statement",
        defaultFileList: this.state.threeMonthBankStatementUrl
          ? [
            {
              uid: "1",
              name:
                this.state.threeMonthBankStatementUrl.split("/").pop() ||
                "3 Month Bank Statement",
              status: "done",
              url: this.state.threeMonthBankStatementUrl,
            },
          ]
          : [],
        changeHandler: (combinedInfo: string) =>
          this.handleFileUploadChange(
            "threeMonthBankStatementUrl",
            combinedInfo,
          ),
        onFileDelete: this.handleDelete,
        fileUploadProps: {
          accept: ".pdf,application/pdf",
        },
        colConfig: { span: 10 },
      },
      {
        id: "threeMomoStatementUrl",
        name: "threeMomoStatementUrl",
        inputType: "fileUpload",
        rule: "string",
        initialValue: this.state.threeMomoStatementUrl,
        required: false,
        disabled: false,
        label: "3 Month MoMo Statement",
        defaultFileList: this.state.threeMomoStatementUrl
          ? [
            {
              uid: "1",
              name:
                this.state.threeMomoStatementUrl.split("/").pop() ||
                "3 Month MoMo Statement",
              status: "done",
              url: this.state.threeMomoStatementUrl,
            },
          ]
          : [],
        changeHandler: (combinedInfo: string) =>
          this.handleFileUploadChange("threeMomoStatementUrl", combinedInfo),
        onFileDelete: this.handleDelete,
        fileUploadProps: {
          accept: ".pdf,application/pdf",
        },
        colConfig: { span: 10 },
      },
      {
        id: "oneYearConsistentTier2ContributionUrl",
        name: "oneYearConsistentTier_2ContributionUrl",
        inputType: "fileUpload",
        rule: "string",
        initialValue: this.state.oneYearConsistentTier2ContributionUrl,
        required: false,
        disabled: false,
        label: "1 Year Tier 2 Contribution",
        defaultFileList: this.state.oneYearConsistentTier2ContributionUrl
          ? [
            {
              uid: "1",
              name:
                this.state.oneYearConsistentTier2ContributionUrl
                  .split("/")
                  .pop() || "1 Year Tier 2 Contribution",
              status: "done",
              url: this.state.oneYearConsistentTier2ContributionUrl,
            },
          ]
          : [],
        changeHandler: (combinedInfo: string) =>
          this.handleFileUploadChange(
            "oneYearConsistentTier2ContributionUrl",
            combinedInfo,
          ),
        onFileDelete: this.handleDelete,
        fileUploadProps: {
          accept: ".pdf,application/pdf",
        },
        colConfig: { span: 10 },
      },
      {
        id: "clearanceFormUrl",
        name: "clearanceFormUrl",
        inputType: "fileUpload",
        rule: "string",
        initialValue: this.state.clearanceFormUrl,
        required: false,
        disabled: false,
        label: "Clearance Form",
        defaultFileList: this.state.clearanceFormUrl
          ? [
            {
              uid: "1",
              name:
                this.state.clearanceFormUrl.split("/").pop() ||
                "Clearance Form",
              status: "done",
              url: this.state.clearanceFormUrl,
            },
          ]
          : [],
        changeHandler: (combinedInfo: string) =>
          this.handleFileUploadChange("clearanceFormUrl", combinedInfo),
        onFileDelete: this.handleDelete,
        fileUploadProps: {
          accept: ".pdf,application/pdf",
        },
        colConfig: { span: 10 },
      },
      {
        id: "settlementQuotationUrl",
        name: "settlementQuotationUrl",
        inputType: "fileUpload",
        rule: "string",
        initialValue: this.state.settlementQuotationUrl,
        required: false,
        disabled: false,
        label: "Settlement Quotation",
        defaultFileList: this.state.settlementQuotationUrl
          ? [
            {
              uid: "1",
              name:
                this.state.settlementQuotationUrl.split("/").pop() ||
                "Settlement Quotation",
              status: "done",
              url: this.state.settlementQuotationUrl,
            },
          ]
          : [],
        changeHandler: (combinedInfo: string) =>
          this.handleFileUploadChange("settlementQuotationUrl", combinedInfo),
        onFileDelete: this.handleDelete,
        fileUploadProps: {
          accept: ".pdf,application/pdf",
        },
        colConfig: { span: 10 },
      },
      {
        id: "sixMonthsBankStatementUrl",
        name: "sixMonthsBankStatementUrl",
        inputType: "fileUpload",
        rule: "string",
        initialValue: this.state.sixMonthsBankStatementUrl,
        required: false,
        disabled: false,
        label: "6 Months Bank Statement",
        defaultFileList: this.state.sixMonthsBankStatementUrl
          ? [
            {
              uid: "1",
              name:
                this.state.sixMonthsBankStatementUrl.split("/").pop() ||
                "6 Months Bank Statement",
              status: "done",
              url: this.state.sixMonthsBankStatementUrl,
            },
          ]
          : [],
        changeHandler: (combinedInfo: string) =>
          this.handleFileUploadChange(
            "sixMonthsBankStatementUrl",
            combinedInfo,
          ),
        onFileDelete: this.handleDelete,
        fileUploadProps: {
          accept: ".pdf,application/pdf",
        },
        colConfig: { span: 10 },
      },
      {
        id: "sixMonthsPayslipsUrl",
        name: "sixMonthsPayslipsUrl",
        inputType: "fileUpload",
        rule: "string",
        initialValue: this.state.sixMonthsPayslipsUrl,
        required: false,
        disabled: false,
        label: "6 Months Payslips",
        defaultFileList: this.state.sixMonthsPayslipsUrl
          ? [
            {
              uid: "1",
              name:
                this.state.sixMonthsPayslipsUrl.split("/").pop() ||
                "6 Months Payslips",
              status: "done",
              url: this.state.sixMonthsPayslipsUrl,
            },
          ]
          : [],
        changeHandler: (combinedInfo: string) =>
          this.handleFileUploadChange("sixMonthsPayslipsUrl", combinedInfo),
        onFileDelete: this.handleDelete,
        fileUploadProps: {
          accept: ".pdf,application/pdf",
        },
        colConfig: { span: 10 },
      },
      {
        id: "employmentOfferLetterUrl",
        name: "employmentOfferLetterUrl",
        inputType: "fileUpload",
        rule: "string",
        initialValue: this.state.employmentOfferLetterUrl,
        required: false,
        disabled: false,
        label: "Employment Offer Letter",
        defaultFileList: this.state.employmentOfferLetterUrl
          ? [
            {
              uid: "1",
              name:
                this.state.employmentOfferLetterUrl.split("/").pop() ||
                "Employment Offer Letter",
              status: "done",
              url: this.state.employmentOfferLetterUrl,
            },
          ]
          : [],
        changeHandler: (combinedInfo: string) =>
          this.handleFileUploadChange("employmentOfferLetterUrl", combinedInfo),
        onFileDelete: this.handleDelete,
        fileUploadProps: {
          accept: ".pdf,application/pdf",
        },
        colConfig: { span: 10 },
      },
      {
        id: "borrowersMandateUrl",
        name: "borrowersMandateUrl",
        inputType: "fileUpload",
        rule: "string",
        initialValue: this.state.borrowersMandateUrl,
        required: false,
        disabled: false,
        label: "Borrower's Mandate",
        defaultFileList: this.state.borrowersMandateUrl
          ? [
            {
              uid: "1",
              name:
                this.state.borrowersMandateUrl.split("/").pop() ||
                "Borrower's Mandate",
              status: "done",
              url: this.state.borrowersMandateUrl,
            },
          ]
          : [],
        changeHandler: (combinedInfo: string) =>
          this.handleFileUploadChange("borrowersMandateUrl", combinedInfo),
        onFileDelete: this.handleDelete,
        fileUploadProps: {
          accept: ".pdf,application/pdf",
        },
        colConfig: { span: 10 },
      },
      {
        id: "guarantorFormsUrl",
        name: "guarantorFormsUrl",
        inputType: "fileUpload",
        rule: "string",
        initialValue: this.state.guarantorFormsUrl,
        required: false,
        disabled: false,
        label: "Guarantor Forms",
        defaultFileList: this.state.guarantorFormsUrl
          ? [
            {
              uid: "1",
              name:
                this.state.guarantorFormsUrl.split("/").pop() ||
                "Guarantor Forms",
              status: "done",
              url: this.state.guarantorFormsUrl,
            },
          ]
          : [],
        changeHandler: (combinedInfo: string) =>
          this.handleFileUploadChange("guarantorFormsUrl", combinedInfo),
        onFileDelete: this.handleDelete,
        fileUploadProps: {
          accept: ".pdf,application/pdf",
        },
        colConfig: { span: 10 },
      },
    ];

    const filteredDocumentFields =
      requiredDocuments.length > 0
        ? documentFields
          .filter((field) => requiredDocuments.includes(field.name as string))
          .map((field, index) => ({
            ...field,
            colConfig: {
              span: 10,
              offset: index % 2 === 1 ? 1 : 0,
            },
          }))
        : [];

    return (
      <Fragment>
        <Spin spinning={isLoading}>
          <Row>
            <Col span={8} style={{ margin: "0 20px" }}>
              <BaseForm
                reference={this.reference}
                fieldsToHide={[]}
                layout="vertical"
                fields={fields}
              />
            </Col>

            <Col span={14}>
              {loanBreakdownData && !updateLoanError && (
                <LoanEligibilityCard loanBreakdownData={loanBreakdownData} />
              )}
              {loanBreakdownError && (
                <Alert
                  message="Loan Breakdown Error"
                  description={loanBreakdownError}
                  type="error"
                  showIcon
                  closable
                  style={{ marginBottom: 16 }}
                  onClose={() =>
                    this.setState({ loanBreakdownError: undefined })
                  }
                />
              )}

              <Collapse
                style={{ marginTop: 16 }}
                defaultActiveKey={["documents"]}
              >
                <Collapse.Panel header="Supporting Documents" key="documents">
                  <BaseForm
                    layout="vertical"
                    reference={this.documentFormRef}
                    fields={filteredDocumentFields}
                    fieldsToHide={[]}
                  />
                </Collapse.Panel>
              </Collapse>

              <Row gutter={16} style={{ margin: "24px 0" }}>
                <Col span={8}>
                  <Button
                    onClick={this.checkLoanBreakdown}
                    loading={isCheckingLoanBreakdown}
                    style={{ width: "100%" }}
                    disabled={isLoading}
                  >
                    Check Loan Eligibility
                  </Button>
                </Col>
                {loanBreakdownData?.data?.eligible_for_loan &&
                  !updateLoanError && (
                    <Col span={8}>
                      <Button
                        type="primary"
                        onClick={this.updateLoanRequest}
                        loading={isLoading}
                        style={{ width: "100%" }}
                      >
                        Update Loan
                      </Button>
                    </Col>
                  )}
              </Row>
            </Col>
          </Row>

          {showCancelModal &&
            ModalCreator({
              title: "Cancel",
              okType: "primary",
              okText: "Yes",
              width: 400,
              showModal: true,
              onOkay: this.cancelRequest,
              toggleModal: this.toggleCancelModal,
              showFooter: true,
              inscription: `Are you sure you want to cancel this request?`,
            })}
        </Spin>
      </Fragment>
    );
  }
}

export default withRouter(EditLoanRequest);
